import { useState, useEffect, lazy, Suspense } from "react";
import { FileText, ArrowRight, ArrowLeft, Eye, EyeOff, Lock, CheckCircle2, Lightbulb, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { navLimitLabel } from "./nav/NavHowItWorks";
import { WORLDS } from "./nav/navShared";
import { isBlockMode } from "../lib/modes";

// Use empty string for same-origin API calls (works on both preview and production)
const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;
const BlocksSolutionView = lazy(() => import("./blocks/BlocksSolutionView").then((m) => ({ default: m.BlocksSolutionView })));

export default function QuestionCard({ 
  question, 
  showNavigation, 
  onNext, 
  onPrev, 
  currentIndex, 
  totalCount,
  user,
  subscriptionTier,
  onLoginRequired,
  onAnswerRevealed,  // Callback when user reveals answer
  showRevealAnswer = true,  // Control whether to show reveal answer button (hidden in drill mode)
  compact = false,  // Full-width (stacked) layout: input/output formats side by side to save height
  progress = null  // { done, total } -> "Warm Up Question 1 of 3" in the header label
}) {
  const [showAnswer, setShowAnswer] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [hint, setHint] = useState(null);
  const [loadingHint, setLoadingHint] = useState(false);
  const [sampleSolution, setSampleSolution] = useState(null);
  const [blocksSolution, setBlocksSolution] = useState(null);
  const [loadingAnswer, setLoadingAnswer] = useState(false);
  const [revealsRemaining, setRevealsRemaining] = useState(null);

  const isSubscriber = subscriptionTier === "student" || subscriptionTier === "school";

  // Fetch reveals remaining when user changes or component mounts
  useEffect(() => {
    if (user) {
      fetchRevealsRemaining();
    } else {
      setRevealsRemaining(null);
    }
  }, [user]);

  // Reset answer display when question changes
  useEffect(() => {
    setShowAnswer(false);
    setSampleSolution(null);
    setBlocksSolution(null);
    setShowHint(false);
    setHint(null);
  }, [question?.question_id]);

  const fetchRevealsRemaining = async () => {
    try {
      const response = await fetch(`${API}/user/reveals-remaining`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setRevealsRemaining(data.reveals_remaining);
      }
    } catch (error) {
      console.error("Error fetching reveals remaining:", error);
    }
  };

  const handleShowAnswer = async () => {
    if (showAnswer) {
      setShowAnswer(false);
      return;
    }

    // For challenge questions, require login
    if (question?.pool === "challenge" && !user) {
      if (onLoginRequired) {
        onLoginRequired();
      }
      return;
    }

    // For logged-in users, check if they have reveals remaining
    if (user && revealsRemaining !== null && revealsRemaining <= 0) {
      toast.error("You've used your answer reveal for today. Try again tomorrow!");
      return;
    }

    setLoadingAnswer(true);
    try {
      const response = await fetch(`${API}/questions/${question.question_id}/answer`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setSampleSolution(data.sample_solution);
        setBlocksSolution(data.blocks_solution || null);
        setShowAnswer(true);
        // Update reveals remaining from response
        if (data.reveals_remaining !== undefined && data.reveals_remaining !== null) {
          setRevealsRemaining(data.reveals_remaining);
        }
        // Notify parent that answer was revealed for this question
        if (onAnswerRevealed && question?.question_id) {
          onAnswerRevealed(question.question_id);
        }
      } else if (response.status === 401) {
        if (onLoginRequired) {
          onLoginRequired();
        }
      } else if (response.status === 429) {
        const data = await response.json();
        toast.error(data.detail || "No reveals remaining today");
        setRevealsRemaining(0);
      }
    } catch (error) {
      console.error("Error fetching answer:", error);
    } finally {
      setLoadingAnswer(false);
    }
  };

  const handleShowHint = async () => {
    if (showHint) {
      setShowHint(false);
      return;
    }

    // For warm-up questions (any pool except 'challenge'), hints are free for everyone
    const isWarmUp = question?.pool !== "challenge";
    
    // For challenge questions, require login and subscription
    if (!isWarmUp) {
      if (!user) {
        if (onLoginRequired) {
          onLoginRequired();
        }
        return;
      }

      if (!isSubscriber) {
        // This shouldn't be reached since the button shows "Upgrade for Challenge Hints" instead
        // But keeping as fallback
        return;
      }
    }

    setLoadingHint(true);
    try {
      const response = await fetch(`${API}/questions/${question.question_id}/hint`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setHint(data.hint);
        setShowHint(true);
      } else if (response.status === 403) {
        toast.error("Unable to load hint. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching hint:", error);
    } finally {
      setLoadingHint(false);
    }
  };

  const getRevealButtonText = () => {
    if (loadingAnswer) {
      return "Loading...";
    }
    if (showAnswer) {
      return (
        <>
          <EyeOff className="w-4 h-4" />
          Hide Answer
        </>
      );
    }
    if (isChallenge && !user) {
      return (
        <>
          <Lock className="w-4 h-4" />
          Login to Reveal Answer
        </>
      );
    }
    // For logged-in users, show remaining count
    if (user && revealsRemaining !== null) {
      if (revealsRemaining <= 0) {
        return (
          <>
            <Lock className="w-4 h-4" />
            No Reveals Left Today
          </>
        );
      }
      return (
        <>
          <Eye className="w-4 h-4" />
          Reveal Answer ({revealsRemaining} remaining)
        </>
      );
    }
    // Anonymous users
    return (
      <>
        <Eye className="w-4 h-4" />
        Show Answer
      </>
    );
  };

  if (!question) {
    return (
      <div className="question-card animate-gentle-pulse">
        <div className="h-6 bg-muted rounded w-1/3 mb-4" />
        <div className="h-4 bg-muted rounded w-full mb-2" />
        <div className="h-4 bg-muted rounded w-2/3" />
      </div>
    );
  }

  const isChallenge = question.pool === "challenge";
  const alreadyAnswered = question.already_answered;
  const isNav = question.mode === "junior" || question.blocks?.kind === "nav";
  // Hide the hint toggle when the payload tells us there is no hint (key present but blank)
  const hasHint = question.hint === undefined || Boolean(String(question.hint || "").trim());

  // Hint / Reveal buttons: bottom row normally, tucked into the slim header for navigation questions
  const actionButtons = (
    <>
      {/* Hint Button - free for warm-up questions (any pool except 'challenge'), subscribers for all */}
      {!hasHint ? null : (question?.pool !== "challenge" || isSubscriber) ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleShowHint}
          disabled={loadingHint}
          className="gap-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
          data-testid="show-hint-btn"
        >
          {loadingHint ? (
            "Loading..."
          ) : showHint ? (
            <>
              <Lightbulb className="w-4 h-4 fill-current" />
              Hide Hint
            </>
          ) : (
            <>
              <Lightbulb className="w-4 h-4" />
              Show Hint
            </>
          )}
        </Button>
      ) : user ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => window.location.href = '/plans'}
          className="gap-2 text-amber-600/50 hover:text-amber-600"
          data-testid="upgrade-for-hint-btn"
        >
          <Lock className="w-4 h-4" />
          Upgrade for Challenge Hints
        </Button>
      ) : null}

      {/* Reveal Answer Button - hidden in drill mode */}
      {showRevealAnswer && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleShowAnswer}
          disabled={loadingAnswer || (user && revealsRemaining !== null && revealsRemaining <= 0 && !showAnswer)}
          className="gap-2 text-muted-foreground hover:text-foreground"
          data-testid="show-answer-btn"
        >
          {getRevealButtonText()}
        </Button>
      )}
    </>
  );

  return (
    <div className={`question-card ${isNav ? "!py-4" : ""}`} data-testid="question-card" data-nav={isNav ? "true" : undefined}>
      {/* Already Answered Badge */}
      {alreadyAnswered && (
        <div className="mb-4 -mt-2">
          <Badge variant="outline" className="gap-1.5 text-success border-success/50 bg-success/5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Previously Completed
          </Badge>
        </div>
      )}
      
      {/* Header */}
      <div className={`flex items-start justify-between gap-4 ${isNav ? "flex-wrap" : "mb-6"}`}>
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${alreadyAnswered ? 'bg-success/10' : 'bg-primary/10'}`}>
            {alreadyAnswered ? (
              <CheckCircle2 className="w-5 h-5 text-success" />
            ) : (
              <FileText className="w-5 h-5 text-primary" />
            )}
          </div>
          <div>
            <h2 className="font-heading font-semibold text-xl sm:text-2xl" data-testid="question-title">
              {question.title}
            </h2>
            <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
              <span className="text-xs text-muted-foreground uppercase tracking-wider" data-testid="question-kind">
                {question.pool === "challenge" ? "Bonus Challenge" : "Warm Up Question"}
                {progress?.total > 0 && (
                  <span className="ml-1.5 font-semibold text-foreground" data-testid="question-progress">
                    {Math.min(progress.done + 1, progress.total)} of {progress.total}
                  </span>
                )}
              </span>
              {question.linear_progress && question.linear_progress.total > 0 && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full w-fit bg-amber-500/10 text-amber-600 dark:text-amber-400" data-testid="campaign-progress" title={`Your place in the ${question.pool === "challenge" ? "bonus" : "warm-up"} campaign${question.linear_progress.lap > 1 ? ` - lap ${question.linear_progress.lap}: same order again, re-answer bonus points` : ""}`}>
                  {question.linear_progress.lap > 1 ? `Lap ${question.linear_progress.lap} · ` : ""}Campaign {question.linear_progress.position} of {question.linear_progress.total}
                </span>
              )}
              {isNav && question.blocks?.nav?.world && question.blocks.nav.world !== "mines" && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full w-fit inline-flex items-center gap-1 bg-sky-500/10 text-sky-700 dark:text-sky-300" data-testid="nav-world-badge">
                  <img src={WORLDS[question.blocks.nav.world]?.sprites.vehicle} alt="" className="w-3.5 h-3.5" /> {WORLDS[question.blocks.nav.world]?.name} world
                </span>
              )}
              {isNav && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary w-fit" data-testid="nav-block-limit">
                  {navLimitLabel(question.blocks?.nav)}{question.blocks?.nav?.max_blocks ? " · loops, ifs and function definitions count as 2" : ""}
                </span>
              )}
              {question.created_by_nickname && (
                <span className="text-xs text-muted-foreground" data-testid="question-creator">
                  <span className="hidden sm:inline">•</span> Created by: <span className="text-primary font-medium">{question.created_by_nickname}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {isNav && <div className="flex flex-wrap gap-2 ml-auto" data-testid="nav-card-actions">{actionButtons}</div>}
        
        {showNavigation && totalCount > 1 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <button
              onClick={onPrev}
              disabled={currentIndex === 0}
              className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="prev-question-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <span>{currentIndex + 1} / {totalCount}</span>
            <button
              onClick={onNext}
              disabled={currentIndex === totalCount - 1}
              className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="next-question-btn"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Description - navigation questions show the game instead */}
      {!isNav && (
        <p className="text-foreground leading-relaxed mb-6 whitespace-pre-wrap" data-testid="question-description">
          {question.description}
        </p>
      )}

      {isNav ? null : (
        <>
        {/* Input / Output Format - only shown if they exist (side by side in compact layout) */}
        <div className={compact ? "grid sm:grid-cols-2 gap-4 mb-6" : ""} data-testid={compact ? "question-formats-compact" : undefined}>
          {question.input_format && (
            <div className={compact ? "" : "mb-4"}>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Input Format
              </h3>
              <p className="text-foreground text-sm whitespace-pre-wrap" data-testid="question-input-format">
                {question.input_format}
              </p>
            </div>
          )}
          {question.output_format && (
            <div className={compact ? "" : "mb-6"}>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Output Format
              </h3>
              <p className="text-foreground text-sm whitespace-pre-wrap" data-testid="question-output-format">
                {question.output_format}
              </p>
            </div>
          )}
        </div>

        {/* Example */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Example
          </h3>
          {question.example_input ? (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-muted-foreground block mb-1.5">Input:</span>
                <div className="io-block" data-testid="question-example-input">
                  {question.example_input}
                </div>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block mb-1.5">Output:</span>
                <div className="io-block" data-testid="question-example-output">
                  {question.example_output}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <span className="text-xs text-muted-foreground block mb-1.5">Output:</span>
              <div className="io-block" data-testid="question-example-output">
                {question.example_output}
              </div>
            </div>
          )}
        </div>
        </>
      )}

      {/* Show Hint & Answer Buttons (navigation questions keep them in the header) */}
      {!isNav && (
        <div className="pt-4 border-t border-border flex flex-wrap gap-2">
          {actionButtons}
        </div>
      )}

      {/* Hint Display */}
      {showHint && hint && (
        <div className="mt-4">
          <span className="text-xs text-amber-600 font-medium block mb-1.5">Hint:</span>
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm" data-testid="hint-content">
            {hint}
          </div>
        </div>
      )}

      {/* Sample Solution Display */}
      {showAnswer && sampleSolution && (
        <div className="mt-4">
          <span className="text-xs text-muted-foreground block mb-1.5">Sample Solution:</span>
          {isBlockMode(question?.mode) && blocksSolution ? (
            <Suspense fallback={<div className="h-32 rounded-xl border border-border flex items-center justify-center text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading blocks…</div>}>
              <div data-testid="sample-solution">
                <BlocksSolutionView workspace={blocksSolution.workspace} inputs={question.blocks?.inputs || []} python={blocksSolution.python || sampleSolution} />
              </div>
            </Suspense>
          ) : (
            <div className="io-block bg-success/5 border border-success/20" data-testid="sample-solution">
              <pre className="whitespace-pre-wrap">{sampleSolution}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
